/**
 * EventFlowCard — chronological event workflow visualization.
 *
 * Redesign (plan of record: ../EVENT_FLOW_REDESIGN.md). The timeline lays the
 * lifecycle steps out in the order their timestamps ACTUALLY occurred rather than
 * a fixed canonical order, so out-of-order completion (very commonly: cleared
 * before it was accepted) is shown truthfully instead of being hidden by fixed
 * slots. The canonical KPIs are decoupled into a separate metrics row so an
 * ordering quirk never becomes a rendering problem. Phone calls placed during the
 * event are dropped onto the timeline as markers with a chip lane below
 * (Grandstream campuses only, and only when calls exist).
 *
 * Props:
 *   eventData     — main event record (campus-local *_datetime_local fields)
 *   tzOffset      — campus tz offset (signed hours) for localizing UTC call times
 *   isGrandstream — gate for the calls lane and the Time to Connect metric
 *   calls         — array of call-log records for this event (UTC call_start_time)
 *   isCloud       — campus is_cloud; on cloud an auto-cleared alert (reason_id -1)
 *                   gets the Auto-cleared chip and the "auto" step badge
 */

import { Fragment, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import { TIME_TO_CONNECT_NA, TIME_TO_CONNECT_NO_ANSWER } from '../../../reports/alert-details/timeToConnect.js';
import {
    callSeconds, isAnsweredCall, isPlacedCall, ringSeconds,
    callOutcomeLabel, callEndReasonLabel, callOutcomeTone,
} from '../../../../utils/callOutcome.js';

const DASH = '—';

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

/** Millis for a campus-local SQL datetime string (null when absent/invalid). */
const toMillisLocal = (sql) => {
    if (!sql) return null;
    const dt = DateTime.fromSQL(sql);
    return dt.isValid ? dt.toMillis() : null;
};

/**
 * Call times arrive as UTC SQL strings while step times are already campus-local.
 * Shift the call into the same local frame (local = UTC + tz_offset, as in
 * convertIsoUTCToLocal) so it can be compared/interpolated against the step timestamps.
 */
const callToMillis = (sqlUtc, offset) => {
    if (!sqlUtc) return null;
    let dt = DateTime.fromSQL(sqlUtc);
    if (!dt.isValid) return null;
    const off = Number(offset) || 0;
    dt = dt.plus({ hours: off });  // tz_offset is signed hours from UTC
    return dt.toMillis();
};

/** Time-only clock label for a step timestamp, e.g. "3:04PM" (date lives in the header). */
const fmtClock = (sql) => {
    if (!sql) return DASH;
    const dt = DateTime.fromSQL(sql);
    return dt.isValid ? dt.toFormat('h:mma') : DASH;
};

/** Time-only clock label for a UTC call time, localized via the campus offset. */
const callClock = (sqlUtc, offset) => {
    if (!sqlUtc) return DASH;
    let dt = DateTime.fromSQL(sqlUtc);
    if (!dt.isValid) return DASH;
    const off = Number(offset) || 0;
    dt = dt.plus({ hours: off });  // tz_offset is signed hours from UTC
    return dt.toFormat('h:mma');
};

/**
 * Human-friendly call duration from a "HH:MM:SS" (or "MM:SS" / seconds) string.
 * e.g. "00:00:11" → "11s", "00:01:05" → "1m 5s", "01:02:03" → "1h 2m".
 */
const formatCallLength = (val) => {
    if (val == null || val === '') return '';
    const secs = callSeconds(val);
    if (secs == null) return String(val);
    if (secs < 60) return `${secs}s`;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

/** Compact elapsed label for a millisecond span, e.g. "45s", "5m", "1h 5m". */
const fmtGap = (ms) => {
    if (ms == null || ms < 0) return null;
    const totalSec = Math.round(ms / 1000);
    if (totalSec < 60) return `${totalSec}s`;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h > 0) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
    return s > 0 && m < 10 ? `${m}m ${s}s` : `${m}m`;
};

// ---------------------------------------------------------------------------
// Step icons — fixed identity per step so it's recognizable wherever it lands.
// ---------------------------------------------------------------------------
const StepIcon = ({ paths }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={{ flexShrink: 0 }}>
        {paths.map((p, i) => <path key={i} d={p} />)}
    </svg>
);

const ICONS = {
    event: <StepIcon paths={['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0']} />,
    accept: <StepIcon paths={['M20 6 9 17l-5-5']} />,
    clear: <StepIcon paths={['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4']} />,
    finish: <StepIcon paths={['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7']} />,
};

const InfoGlyph = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
         style={{ marginLeft: 3, verticalAlign: 'middle', opacity: 0.7 }}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
    </svg>
);

// Call types that count as connecting with the resident: 1 Caregiver to Area
// Two-way, 3 Caregiver to Resident Two-way, 4 Caregiver to Areas One-way.
// Never 2 (Caregiver to Caregiver). Same list as sp_getEventFirstConnect.
const CONNECT_CALL_TYPES = new Set([1, 3, 4]);

/**
 * Was this alert cleared by the system rather than a caregiver? Cloud campuses
 * record that as reason_id -1; non-cloud auto-close records 0, which can't be told
 * apart from a real clear, so it is never flagged there (same rule as the cloud
 * report proc). sp_getEvent's autoclear_datetime is NOT usable for this: it is null
 * on real auto-clears and 1970-01-01 on some normal events.
 */
const isAutoCleared = (d, isCloud) => isCloud && d?.reason_id != null && Number(d.reason_id) === -1;

const EventFlowCard = ({ eventData, tzOffset, isGrandstream, calls, isCloud = false }) => {
    const [hoveredKpi, setHoveredKpi] = useState(null);
    const [hoveredCall, setHoveredCall] = useState(null);

    // -----------------------------------------------------------------------
    // Build the lifecycle model (steps, ordering, connectors, KPIs, chips).
    // -----------------------------------------------------------------------
    const model = useMemo(() => {
        const d = eventData || {};
        const raw = {
            event: d.event_datetime_local || null,
            accept: d.accept_datetime_local || null,
            // NOTE: ack_datetime_local is the CLEARED time, NOT accepted. "ack"
            // reads like "acknowledge/accept" but it is the clear step — do not
            // wire it to the Accepted node.
            clear: d.ack_datetime_local || null,
            finish: d.finish_datetime_local || null,
        };
        const autoclear = isAutoCleared(d, isCloud);

        const CANON = [
            { key: 'event', label: 'Event Start', order: 0 },
            { key: 'accept', label: 'User Accepted', order: 1 },
            { key: 'clear', label: 'Device Cleared', order: 2 },
            { key: 'finish', label: 'Event Finished', order: 3 },
        ];

        const ms = {
            event: toMillisLocal(raw.event),
            accept: toMillisLocal(raw.accept),
            clear: toMillisLocal(raw.clear),
            finish: toMillisLocal(raw.finish),
        };

        const isFinished = !!raw.finish;

        // Classify each canonical step: done / never (skipped) / pending (still coming).
        const steps = CANON.map((c) => {
            const done = !!raw[c.key];
            let state = 'done';
            if (!done) {
                const laterDone = CANON.some((o) => o.order > c.order && !!raw[o.key]);
                // A later step already happened, or the event is finished → this
                // step was skipped ("never"). Otherwise it is still legitimately
                // coming ("pending").
                state = (laterDone || isFinished) ? 'never' : 'pending';
            }
            return {
                ...c,
                time: raw[c.key],
                ms: ms[c.key],
                state,
                autocleared: c.key === 'clear' && done && autoclear,
            };
        });

        // Once the canonical order is broken by a skip, "Pending" (still coming
        // in order) no longer makes sense — a step that never got accepted isn't
        // meaningfully "about to finish". Downgrade any remaining pending steps to
        // "never" (rendered as "—") so we don't show a misleading "Pending".
        if (steps.some((s) => s.state === 'never')) {
            steps.forEach((s) => { if (s.state === 'pending') s.state = 'never'; });
        }

        // "Jumped the queue": a done step whose timestamp precedes a
        // canonically-earlier done step (flag only the one that jumped).
        steps.forEach((s) => {
            s.flagged = s.state === 'done' && steps.some((o) =>
                o.state === 'done' && o.order < s.order &&
                o.ms != null && s.ms != null && o.ms > s.ms);
        });

        // Ordering key: done → actual time; never → just before the earliest
        // later done step (so it reads as "skipped here"); pending → appended in
        // canonical order after everything actual.
        const doneSteps = steps.filter((s) => s.state === 'done');
        steps.forEach((s) => {
            if (s.state === 'done') {
                s.sortKey = s.ms;
            } else if (s.state === 'never') {
                const laterMs = doneSteps.filter((o) => o.order > s.order).map((o) => o.ms);
                s.sortKey = laterMs.length ? Math.min(...laterMs) - 0.5 : Number.MAX_SAFE_INTEGER;
            } else {
                s.sortKey = Number.MAX_SAFE_INTEGER + s.order;
            }
        });
        const ordered = [...steps].sort((a, b) => (a.sortKey - b.sortKey) || (a.order - b.order));
        const byKey = Object.fromEntries(steps.map((s) => [s.key, s]));
        const orderedIndex = Object.fromEntries(ordered.map((s, i) => [s.key, i]));

        // Connectors between consecutive ordered nodes — bare elapsed gaps.
        const connectors = [];
        for (let i = 0; i < ordered.length - 1; i++) {
            const a = ordered[i], b = ordered[i + 1];
            const spanning = a.state === 'done' && b.state === 'done' && a.ms != null && b.ms != null;
            connectors.push({
                leftKey: a.key,
                rightKey: b.key,
                spanning,
                gapMs: spanning ? b.ms - a.ms : null,
            });
        }

        // KPIs — from canonical timestamps, guarded (start && end && end >= start).
        const kpiVal = (startKey, endKey) => {
            const s = ms[startKey], e = ms[endKey];
            if (s == null || e == null) return { value: null };
            if (e < s) return { value: null, reason: 'Timestamps are out of order' };
            return { value: fmtGap(e - s) };
        };
        // Reason wording keyed off the raw timestamps (not the display state
        // above) so it stays accurate: a step is genuinely "never" only once a
        // later canonical step has happened; otherwise it's simply not yet reached.
        const acceptReason = (raw.clear || raw.finish) ? 'Event was never accepted' : 'Not yet accepted';
        const clearReason = raw.finish ? 'Event was never cleared' : 'Not yet cleared';
        const finishReason = 'Not yet finished';

        const kpis = [];
        const tta = kpiVal('event', 'accept');
        kpis.push({ key: 'timeToAccept', label: 'Time to accept', span: ['event', 'accept'],
            value: tta.value, reason: tta.value ? null : (tta.reason || acceptReason) });

        const resp = kpiVal('event', 'clear');
        kpis.push({ key: 'response', label: 'Response time', span: ['event', 'clear'],
            value: resp.value, reason: resp.value ? null : (resp.reason || clearReason) });

        const svc = kpiVal('clear', 'finish');
        kpis.push({ key: 'service', label: 'Service time', span: ['clear', 'finish'],
            value: svc.value,
            reason: svc.value ? null : (svc.reason || (byKey.clear.state !== 'done' ? clearReason : finishReason)) });

        // Total: event → finish; fall back to event → clear when not finished,
        // relabelled so the denominator isn't silently different on the same label.
        const total = kpiVal('event', 'finish');
        if (total.value) {
            kpis.push({ key: 'total', label: 'Total time', span: ['event', 'finish'],
                value: total.value, reason: null });
        } else {
            const totalToClear = kpiVal('event', 'clear');
            if (totalToClear.value) {
                kpis.push({ key: 'total', label: 'Total (to cleared)', span: ['event', 'clear'],
                    value: totalToClear.value, reason: 'Event not finished — measured to cleared' });
            } else {
                kpis.push({ key: 'total', label: 'Total time', span: ['event', 'finish'],
                    value: null, reason: total.reason || finishReason });
            }
        }

        // Workflow status chip — a single, prioritized status in the header.
        // Amber for a workflow that broke down; green when it completed cleanly.
        // reason_id === -2 is the auto-finish timeout (never properly finished).
        // sp_getEvent returns reason_id; this used to read reason_code_id, which it
        // doesn't return, so -2 never matched and auto-finished events wrongly got
        // "Workflow completed".
        const reasonCodeId = d.reason_id != null ? Number(d.reason_id) : null;
        const chips = [];
        if (byKey.accept.state === 'never') {
            chips.push({ key: 'na', label: 'Not accepted', tone: 'amber' });
        } else if (ms.accept != null && ms.clear != null && ms.clear < ms.accept) {
            chips.push({ key: 'cba', label: 'Cleared before accepted', tone: 'amber' });
        } else if (ms.accept != null && ms.clear != null && reasonCodeId === -2) {
            chips.push({ key: 'nf', label: 'Not finished', tone: 'amber' });
        } else if (
            ms.accept != null && ms.clear != null && ms.finish != null &&
            ms.clear >= ms.accept && ms.finish >= ms.clear && reasonCodeId !== -2
        ) {
            chips.push({ key: 'ok', label: 'Workflow completed', tone: 'success' });
        }
        // A fact rather than a status, so it sits beside the status chip instead of
        // competing with it (EVENT_FLOW_REDESIGN.md: "Auto-cleared (neutral)").
        if (byKey.clear.autocleared) {
            chips.push({ key: 'ac', label: 'Auto-cleared', tone: 'neutral' });
        }

        return { ordered, connectors, kpis, chips, byKey, orderedIndex };
    }, [eventData, isCloud]);

    // -----------------------------------------------------------------------
    // Place call markers into spanning connectors (Grandstream + calls exist).
    // -----------------------------------------------------------------------
    const callMarkers = useMemo(() => {
        if (!isGrandstream) return [];
        const list = Array.isArray(calls) ? calls : [];
        if (!list.length) return [];

        const { ordered } = model;

        // Anchor markers to the DONE steps in timeline order. A range spans across
        // any never/pending node sitting between two done steps, so calls still
        // place (and the lane still renders) even when e.g. a never-accepted node
        // splits Event start and Cleared. A call that can't be positioned keeps a
        // null connIndex — it still appears as a chip, just without a marker.
        const doneSeq = ordered
            .map((s, idx) => ({ ms: s.ms, idx, done: s.state === 'done' }))
            .filter((s) => s.done && s.ms != null);

        const firstMs = doneSeq.length ? doneSeq[0].ms : null;
        const lastMs = doneSeq.length ? doneSeq[doneSeq.length - 1].ms : null;

        const markers = list.map((call, i) => {
            const cms = callToMillis(call.call_start_time, tzOffset);
            let connIndex = null;
            let frac = 0;
            if (doneSeq.length >= 2 && cms != null) {
                if (cms <= firstMs) {
                    // Callback before the timeline starts → pin to leading edge.
                    connIndex = doneSeq[0].idx; frac = 0;
                } else if (cms >= lastMs) {
                    // Callback after the timeline ends → pin to trailing edge.
                    connIndex = doneSeq[doneSeq.length - 1].idx - 1; frac = 1;
                } else {
                    // Find the done pair bracketing the call, then map its time
                    // fraction across the connector(s) between those two nodes.
                    for (let p = 0; p < doneSeq.length - 1; p++) {
                        const A = doneSeq[p], B = doneSeq[p + 1];
                        if (cms >= A.ms && cms <= B.ms) {
                            const segs = B.idx - A.idx;
                            const scaled = (B.ms > A.ms ? (cms - A.ms) / (B.ms - A.ms) : 0) * segs;
                            let seg = Math.floor(scaled);
                            if (seg >= segs) seg = segs - 1;
                            connIndex = A.idx + seg;
                            frac = scaled - seg;
                            break;
                        }
                    }
                }
            }
            return { i, call, connIndex, frac };
        });

        // Fan out markers that land too close together on the same connector
        // (e.g. near-simultaneous calls) so their dots don't stack. Nudge each
        // to keep a minimum gap, then shift the cluster back left if it overran.
        const MIN_GAP = 0.06;
        const byConn = {};
        markers.forEach((m) => { if (m.connIndex != null) (byConn[m.connIndex] ||= []).push(m); });
        Object.values(byConn).forEach((group) => {
            if (group.length < 2) return;
            group.sort((a, b) => a.frac - b.frac);
            for (let k = 1; k < group.length; k++) {
                if (group[k].frac - group[k - 1].frac < MIN_GAP) {
                    group[k].frac = group[k - 1].frac + MIN_GAP;
                }
            }
            const overflow = group[group.length - 1].frac - 0.98;
            if (overflow > 0) {
                group.forEach((m) => { m.frac = Math.max(0.02, m.frac - overflow); });
            }
        });

        return markers;
    }, [isGrandstream, calls, tzOffset, model]);

    const hasCalls = callMarkers.length > 0;

    // -----------------------------------------------------------------------
    // Time to Connect — event start → the first ANSWER of an intercom call (its
    // start + ring time). Without one: "No answer" when an intercom call was placed
    // (answered, no answer or busy), N/A when none was. Operations rule (2026-10-09):
    // never 0 and never the clear, so the auto-clear rule takes no part.
    //
    // Same definition as the reports (docs/TIME_TO_CONNECT_PLAN.md, phase 4), which
    // compute it in sp_getEventFirstConnect (scripts/gen_time_to_connect_procs.py) +
    // attachTimeToConnect. An intercom call is call_type_id 1, 3 or 4, placed at or
    // after event start; answered / placed / ring time come from utils/callOutcome.js,
    // the browser twin of the SQL. MIN over the answer times, not the answer of the
    // earliest call: the first call placed may not be the first answered.
    // -----------------------------------------------------------------------
    const connectInfo = useMemo(() => {
        const eventMs = model.byKey.event?.ms ?? null;
        if (eventMs == null) return { value: null, reason: 'Event has no start time', empty: TIME_TO_CONNECT_NA };

        const list = Array.isArray(calls) ? calls : [];
        let firstAnswer = null; // { ms, index }
        let placed = false;
        list.forEach((c, i) => {
            const tid = parseInt(c?.call_type_id, 10);
            if (!CONNECT_CALL_TYPES.has(tid)) return;
            const cms = callToMillis(c.call_start_time, tzOffset);
            if (cms == null || cms < eventMs) return;
            if (isPlacedCall(c)) placed = true;
            if (!isAnsweredCall(c)) return;
            const answerMs = cms + ringSeconds(c) * 1000;
            if (!firstAnswer || answerMs < firstAnswer.ms) firstAnswer = { ms: answerMs, index: i };
        });

        if (!firstAnswer) {
            return placed
                ? { value: null, reason: 'An intercom call was placed but nobody answered', empty: TIME_TO_CONNECT_NO_ANSWER }
                : { value: null, reason: 'No intercom call on this alert', empty: TIME_TO_CONNECT_NA };
        }
        return { value: fmtGap(firstAnswer.ms - eventMs), eventMs, endMs: firstAnswer.ms, callIndex: firstAnswer.index };
    }, [model, calls, tzOffset]);

    // -----------------------------------------------------------------------
    // KPI-hover → highlight the span it measures on the timeline.
    // -----------------------------------------------------------------------
    const highlight = useMemo(() => {
        const nodes = new Set();
        const conns = new Set();
        const callsSet = new Set();
        if (!hoveredKpi) return { nodes, conns, calls: callsSet };

        // Time to Connect: event → the first intercom call. Highlight the step
        // nodes up to the call time, plus that call's marker.
        if (hoveredKpi === 'timeToConnect') {
            const { eventMs, endMs, callIndex } = connectInfo;
            if (eventMs == null || endMs == null) return { nodes, conns, calls: callsSet };
            const idxs = [];
            model.ordered.forEach((s, i) => {
                if (s.ms != null && s.ms >= eventMs && s.ms <= endMs) { nodes.add(s.key); idxs.push(i); }
            });
            const ei = model.orderedIndex.event;
            if (ei != null) { nodes.add('event'); idxs.push(ei); }
            if (idxs.length) {
                const lo = Math.min(...idxs), hi = Math.max(...idxs);
                for (let i = lo; i < hi; i++) conns.add(i);
            }
            if (callIndex != null) {
                callsSet.add(callIndex);
                const m = callMarkers.find((mm) => mm.i === callIndex);
                if (m && m.connIndex != null) conns.add(m.connIndex);
            }
            return { nodes, conns, calls: callsSet };
        }

        const kpi = model.kpis.find((k) => k.key === hoveredKpi);
        if (!kpi) return { nodes, conns, calls: callsSet };
        const [aKey, bKey] = kpi.span;
        const ai = model.orderedIndex[aKey];
        const bi = model.orderedIndex[bKey];
        if (ai == null || bi == null) return { nodes, conns, calls: callsSet };
        const lo = Math.min(ai, bi), hi = Math.max(ai, bi);
        for (let i = lo; i <= hi; i++) nodes.add(model.ordered[i].key);
        for (let i = lo; i < hi; i++) conns.add(i);
        return { nodes, conns, calls: callsSet };
    }, [hoveredKpi, model, connectInfo, callMarkers]);

    if (!eventData) return null;

    const { ordered, connectors, kpis, chips } = model;

    // Metrics row = canonical KPIs + Time to Connect (needs call data, so it lives
    // outside the eventData-only model). Grandstream campuses only: anywhere else
    // there is no intercom, so it could only ever read N/A.
    const metricKpis = isGrandstream ? [
        ...kpis,
        { key: 'timeToConnect', label: 'Time to connect', value: connectInfo.value, reason: connectInfo.reason,
          empty: connectInfo.empty || TIME_TO_CONNECT_NA },
    ] : kpis;

    // Event date shown once, next to the card title (step/chip labels are time-only).
    const eventDt = eventData.event_datetime_local ? DateTime.fromSQL(eventData.event_datetime_local) : null;
    const eventDate = eventDt && eventDt.isValid ? eventDt.toFormat('MMM d, yyyy') : null;

    /* Hover text: when, length, who, then the outcome and why it ended (new columns;
       a call logged before them shows neither). */
    const callTitle = (c) => {
        const t = c.call_start_time ? convertIsoUTCToLocal(c.call_start_time, tzOffset) : DASH;
        const outcome = callOutcomeLabel(c);
        const reason = callEndReasonLabel(c);
        return [`${t} · ${c.call_length || DASH} · ${c.caller || DASH} → ${c.receiver || DASH}`,
            outcome && `${outcome}${reason ? ` (${reason})` : ''}`,
            isAnsweredCall(c) && callSeconds(c.talk_length) > 0 ? `Talk time ${formatCallLength(c.talk_length)}` : '',
        ].filter(Boolean).join('\n');
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <div className="ep-flow-title-wrap">
                    <h3 className="pp-section__title">Event flow</h3>
                    {eventDate ? <span className="ep-flow-date">{eventDate}</span> : null}
                </div>
                {chips.length > 0 && (
                    <div className="ep-flow-chips">
                        {chips.map((c) => (
                            <span key={c.key} className={`ep-flow-chip ep-flow-chip--${c.tone}`}>{c.label}</span>
                        ))}
                    </div>
                )}
            </div>
            <div className="pp-section__body">
                {/* Chronological timeline */}
                <div className="ep-flow-timeline">
                    {ordered.map((s, idx) => {
                        const stepCls = ['ep-flow-step'];
                        if (s.state !== 'done') stepCls.push('ep-flow-step--pending');
                        if (s.flagged) stepCls.push('ep-flow-step--flagged');
                        if (highlight.nodes.has(s.key)) stepCls.push('ep-flow-step--highlight');
                        const timeText = s.state === 'done' ? fmtClock(s.time)
                            : s.state === 'pending' ? 'Pending' : DASH;

                        const conn = idx < connectors.length ? connectors[idx] : null;
                        const connCls = ['ep-flow-connector'];
                        if (conn && !conn.spanning) connCls.push('ep-flow-connector--pending');
                        if (highlight.conns.has(idx)) connCls.push('ep-flow-connector--highlight');
                        const markers = conn ? callMarkers.filter((m) => m.connIndex === idx) : [];

                        return (
                            <Fragment key={s.key}>
                                <div className={stepCls.join(' ')}>
                                    <div className="ep-flow-step__dot">{ICONS[s.key]}</div>
                                    <div className="ep-flow-step__label">{s.label}</div>
                                    <div className="ep-flow-step__time">{timeText}</div>
                                    {s.autocleared ? <div className="ep-flow-step__badge">auto</div> : null}
                                </div>

                                {conn ? (
                                    <div className={connCls.join(' ')}>
                                        <div className="ep-flow-connector__line">
                                            {markers.map((m) => (
                                                <span
                                                    key={m.i}
                                                    className={`ep-flow-marker${(hoveredCall === m.i || highlight.calls.has(m.i)) ? ' ep-flow-marker--active' : ''}`}
                                                    style={{ left: `${m.frac * 100}%` }}
                                                    onMouseEnter={() => setHoveredCall(m.i)}
                                                    onMouseLeave={() => setHoveredCall(null)}
                                                    title={callTitle(m.call)}
                                                    aria-hidden="true"
                                                />
                                            ))}
                                        </div>
                                        <div className="ep-flow-connector__duration">
                                            {conn.gapMs != null ? fmtGap(conn.gapMs) : DASH}
                                        </div>
                                    </div>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </div>

                {/* Calls lane (Grandstream + only when calls exist) */}
                {hasCalls && (
                    <div className="ep-flow-calls">
                        <span className="ep-flow-calls__label">Calls · {callMarkers.length}</span>
                        <div className="ep-flow-call-chips">
                            {callMarkers.map((m) => (
                                <span
                                    key={m.i}
                                    className={`ep-flow-call-chip${(hoveredCall === m.i || highlight.calls.has(m.i)) ? ' ep-flow-call-chip--active' : ''}`}
                                    onMouseEnter={() => setHoveredCall(m.i)}
                                    onMouseLeave={() => setHoveredCall(null)}
                                    title={callTitle(m.call)}
                                >
                                    <span className="ep-flow-call-chip__who">
                                        {(m.call.caller || DASH)} {'→'} {(m.call.receiver || DASH)}
                                    </span>
                                    {callOutcomeLabel(m.call) ? (
                                        <span className={`ep-flow-call-chip__outcome ep-flow-call-chip__outcome--${callOutcomeTone(m.call)}`}>
                                            {callOutcomeLabel(m.call)}
                                        </span>
                                    ) : null}
                                    <span className="ep-flow-call-chip__meta">
                                        {callClock(m.call.call_start_time, tzOffset)}
                                        {/* Answered: the talk time. Otherwise the call's own length (it rang). */}
                                        {(() => {
                                            const len = isAnsweredCall(m.call) && callSeconds(m.call.talk_length) > 0
                                                ? m.call.talk_length : m.call.call_length;
                                            return formatCallLength(len) ? ` · ${formatCallLength(len)}` : '';
                                        })()}
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Metrics row — canonical KPIs, decoupled from the timeline */}
                <div className="ep-flow-metrics">
                    {metricKpis.map((k) => (
                        <div
                            key={k.key}
                            className={`ep-flow-metric${hoveredKpi === k.key ? ' ep-flow-metric--active' : ''}`}
                            onMouseEnter={() => setHoveredKpi(k.key)}
                            onMouseLeave={() => setHoveredKpi(null)}
                        >
                            <span className="ep-flow-metric__label">{k.label}</span>
                            <span className="ep-flow-metric__value" title={k.reason || undefined}>
                                {k.value || k.empty || DASH}
                                {!k.value && k.reason ? <InfoGlyph /> : null}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EventFlowCard;
